import React, { useCallback } from 'react';
import { Button, Input, Image, message } from 'antd';
import { Image as ImageIcon, Sparkles } from 'lucide-react';
import { useRightPanelContext } from '../RightPanelContext';
import { PANEL_MODE } from '../../constants';
import { convertSingleBlock } from '../../../utils/blockConverter';
import { post } from '../../../../../axios/axios';
import { ResultCard } from '../shared/ResultCard';
import { PreviewActions } from '../shared/PreviewActions';
import styles from '../../../styles/index.module.scss';

const { TextArea } = Input;

const isCanceled = (error) =>
    error?.name === 'AbortError' ||
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED' ||
    error?.message?.includes('canceled');

/**
 * 图片修改面板
 */
const ImagePanel = () => {
    const {
        editor,
        commonState,
        textState,
        imgState,
        findImageNodeByBlockId,
        saveBlockContent,
        handleCancelPreview,
        handleReset,
    } = useRightPanelContext();

    const contentBlockId =
        textState.selectedBlockIds.length > 0 ? textState.selectedBlockIds[0] : null;
    const currentImage = contentBlockId ? findImageNodeByBlockId(editor, contentBlockId) : null;
    const currentSrc = currentImage?.node?.attrs?.src || '';

    const handleGenerate = useCallback(async () => {
        if (!editor || commonState.generating) return;
        if (commonState.showPreview) {
            commonState.showPreview = false;
            imgState.previewImageBlock = null;
            imgState.previewOriginalImageUrl = '';
            commonState.previewMode = '';
        }
        if (!contentBlockId) {
            message.warning('请先选中图片');
            return;
        }

        commonState.generating = true;
        commonState.generationProgress = 20;
        commonState.generationStatus = '正在重新生成图片…';
        try {
            const response = await post('/ai/ai/paper/editor/v2/modify-image', {
                contentBlockId,
                userRequirement: textState.customInstruction.trim(),
            });
            commonState.generationProgress = 100;
            commonState.generationStatus = '';
            commonState.generating = false;

            const newBlock = response?.data?.data || response?.data;
            if (newBlock && newBlock.type === 'image') {
                const imageInfo = findImageNodeByBlockId(editor, contentBlockId);
                imgState.previewOriginalImageUrl = imageInfo?.node?.attrs?.src || '';
                imgState.previewImageBlock = newBlock;
                commonState.showPreview = true;
                commonState.previewMode = 'modify-image';
            } else {
                message.warning('接口返回数据格式不正确');
            }
        } catch (error) {
            if (isCanceled(error)) return;
            commonState.generating = false;
            commonState.generationStatus = '修图失败，请重试';
            console.error('修图接口调用失败:', error);
            message.error(error?.response?.data?.msg || '修图失败，请重试');
        }
    }, [editor, commonState, textState, imgState, findImageNodeByBlockId, contentBlockId]);

    const handleConfirmApply = useCallback(() => {
        if (!editor || !contentBlockId) return;
        try {
            const newImageNode = convertSingleBlock(imgState.previewImageBlock);
            if (!newImageNode) throw new Error('转换新块失败');
            const imageInfo = findImageNodeByBlockId(editor, contentBlockId);
            if (!imageInfo) throw new Error('找不到原图片节点');

            const { node: oldImageNode, pos } = imageInfo;
            editor
                .chain()
                .focus()
                .setTextSelection({ from: pos, to: pos + oldImageNode.nodeSize })
                .deleteSelection()
                .insertContent(newImageNode)
                .run();

            message.success('图片已更新');
            saveBlockContent(contentBlockId);

            commonState.showPreview = false;
            imgState.previewImageBlock = null;
            imgState.previewOriginalImageUrl = '';
            commonState.previewMode = '';
            commonState.mode = PANEL_MODE.EMPTY;
            textState.selectedText = '';
            textState.selectedBlockIds = [];
            textState.customInstruction = '';
            textState.currentAction = '';
            textState.selectedRange = { from: null, to: null };
        } catch (error) {
            console.error('替换图片节点失败:', error);
            message.error(error.message || '替换图片节点失败');
        }
    }, [
        commonState,
        textState,
        imgState,
        editor,
        findImageNodeByBlockId,
        saveBlockContent,
        contentBlockId,
    ]);

    if (commonState.showPreview && imgState.previewImageBlock) {
        return (
            <div className={styles.panelSection}>
                <ResultCard
                    title="修图结果"
                    meta="图片"
                    actions={
                        <PreviewActions
                            onCancel={handleCancelPreview}
                            onRegenerate={handleGenerate}
                            onApply={handleConfirmApply}
                            generating={commonState.generating}
                            applyText="替换图片"
                        />
                    }
                >
                    <div className={styles.previewImageBox}>
                        {imgState.previewImageBlock.content && (
                            <Image
                                src={imgState.previewImageBlock.content}
                                alt="生成图片"
                                preview={{ mask: '点击预览' }}
                            />
                        )}
                    </div>
                </ResultCard>
            </div>
        );
    }

    return (
        <div className={styles.panelSection}>
            <div className={styles.quoteCard}>
                <div className={styles.quoteCardLabel}>
                    <ImageIcon size={14} />
                    已选中图片
                </div>
                {currentSrc && (
                    <div className={styles.quoteCardImage}>
                        <img src={currentSrc} alt="当前图片" />
                    </div>
                )}
            </div>
            <div className={styles.presetDesc}>
                描述你希望怎么改，AI 会基于原图的内容重新生成一张图。
            </div>

            <div className={styles.sectionLabel}>修改要求</div>
            <TextArea
                value={textState.customInstruction}
                onChange={(e) => {
                    textState.customInstruction = e.target.value;
                }}
                placeholder="例如：把线条颜色改成蓝色、增加图例说明…"
                autoSize={{ minRows: 3, maxRows: 6 }}
                disabled={commonState.generating}
                className={styles.instructionInput}
            />

            {commonState.generationStatus && (
                <div className={styles.generationStatus}>{commonState.generationStatus}</div>
            )}

            <Button
                type="primary"
                block
                size="large"
                icon={<Sparkles size={16} />}
                onClick={handleGenerate}
                loading={commonState.generating}
                className={styles.primaryAction}
            >
                {commonState.generating ? 'AI 处理中…' : '开始修图'}
            </Button>
            <Button block type="text" onClick={handleReset} disabled={commonState.generating}>
                取消
            </Button>
        </div>
    );
};

export default ImagePanel;
